// app/root.tsx
import { cssBundleHref } from '@remix-run/css-bundle';
import {
  type HeadersFunction,
  json,
  type LinksFunction,
  type LoaderFunctionArgs,
  type MetaFunction,
} from '@remix-run/node';
import {
  isRouteErrorResponse,
  Links,
  Meta,
  Outlet,
  Scripts,
  ScrollRestoration,
  useLoaderData,
  useLocation,
  useRouteError,
  useRouteLoaderData,
} from '@remix-run/react';
import { captureMessage, captureRemixErrorBoundaryError, withSentry } from '@sentry/remix';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { createInstance } from 'i18next';
import { Container } from 'react-bootstrap';
import { I18nextProvider, initReactI18next } from 'react-i18next';
import skeletonStylesHref from 'react-loading-skeleton/dist/skeleton.css?url';

import montserratBoldHref from './assets/fonts/Montserrat/Montserrat-Bold.woff2?url';
import montserratRegularHref from './assets/fonts/Montserrat/Montserrat-Regular.woff2?url';
import mainStylesHref from './assets/scss/main.scss?url';

import { ProgressBarProvider } from '~/components/helpers/ProgressBar';
import { HydrationWatchdog } from '~/components/HydrationWatchdog';
import IconSprite from '~/components/icons/IconSprite';
import { CustomerProvider } from '~/context/CustomerContext';
import { DropcartProvider } from '~/context/DropcartContext';
import { FlashMessagesProvider } from '~/context/FlashMessagesContext';
import { OrderProvider } from '~/context/OrderContext';
import { SlugTranslationsProvider } from '~/context/SlugTranslationsContext';
import { GTMHeadScript, GTMNoScript, GTMPageTracker } from '~/gtm';
import { useTransientErrorRecovery } from '~/hooks/useTransientErrorRecovery';
import { initClientI18nSync } from '~/i18n';
import { createServerI18n } from '~/i18n.server';
import Layout from '~/layouts/Default';
import csTranslations from '~/locales/cs.json';
import enTranslations from '~/locales/en.json';
import ErrorBoundaryRoute from '~/pages/Error';
import { NotificationBar } from '~/types/NotificationBar';
import type { Taxon } from '~/types/Taxon';
import { rootCacheHeaders, setCacheDecision } from '~/utils/cacheControl.server';
import { orderTokenCookie, parseCookieHeader } from '~/utils/cookies.server';
import { INLINE_STYLES_MARKER } from '~/utils/inlineStyles';
import { defaultLocale, detectLocale } from '~/utils/language';
import {
  buildTitle,
  ogImageMeta,
  SITE_OG_IMAGE_HEIGHT,
  SITE_OG_IMAGE_PATH,
  SITE_OG_IMAGE_TYPE,
  SITE_OG_IMAGE_WIDTH,
} from '~/utils/meta';
import { getRequestOrigin } from '~/utils/origin.server';
import { checkRedirect } from '~/utils/redirect.server';
import { resolveRequestChannel } from '~/utils/requestChannel.server';
import { setChannelConfig } from '~/utils/runtimeConfig';
import { cachedFetch } from '~/utils/serverCache.server';

const fallbackResources: Record<string, Record<string, string>> = {
  cs: { translation: csTranslations },
  en: { translation: enTranslations },
};

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Without this every window focus refires the query, and a cart refetch pulls the
      // variant, its option values and the product for each item — a handful of requests
      // per item. Half a minute of trust is plenty for a shopping cart.
      staleTime: 30_000,
    },
  },
});

const INLINE_STYLES = import.meta.env.PROD;

// -------------------------
// LINKS
// -------------------------
export const links: LinksFunction = () => [
  // Font preloads (before CSS to start loading early)
  {
    rel: 'preload',
    href: montserratRegularHref,
    as: 'font',
    type: 'font/woff2',
    crossOrigin: 'anonymous',
  },
  {
    rel: 'preload',
    href: montserratBoldHref,
    as: 'font',
    type: 'font/woff2',
    crossOrigin: 'anonymous',
  },
  ...(cssBundleHref ? [{ rel: 'stylesheet', href: cssBundleHref }] : []),
  // Production inlines the stylesheets (see <InlineStyles />); dev keeps <link> for CSS HMR.
  ...(INLINE_STYLES
    ? []
    : [
        { rel: 'stylesheet', href: mainStylesHref },
        { rel: 'stylesheet', href: skeletonStylesHref },
      ]),
];

/**
 * The full stylesheet (~47 KB br) goes inline into the document instead of a render-blocking
 * <link>. Behind an HTTP/2 proxy that schedules streams round-robin, the external CSS shared
 * bandwidth with ~20 parallel font/script/image requests and on slow 4G finished around 4 s,
 * so first paint (and the LCP hero, already downloaded) waited for it. Inline, the page paints
 * as soon as the HTML arrives; the extra HTML bytes are far cheaper than that wait.
 *
 * Only a marker is rendered on the server; entry.server.tsx substitutes the CSS while streaming,
 * so the stylesheet never enters the client bundle. React does not compare dangerouslySetInnerHTML
 * during hydration, so the marker/CSS difference does not trigger a mismatch. The client still
 * has to render the real CSS, not the marker: when hydration fails for any reason (a browser
 * extension that edited the DOM before React ran, for instance), React throws the server markup
 * away and renders the root from scratch, and a marker-only <style> would leave the page unstyled.
 * So the client picks the CSS up from the <style> the server already sent.
 */
const inlineStylesHtml =
  typeof document === 'undefined'
    ? INLINE_STYLES_MARKER
    : (document.querySelector<HTMLStyleElement>('style[data-inline-styles]')?.textContent ?? INLINE_STYLES_MARKER);

const InlineStyles = () =>
  INLINE_STYLES ? <style data-inline-styles dangerouslySetInnerHTML={{ __html: inlineStylesHtml }} /> : null;

// -------------------------
// META (default)
// -------------------------
export const meta: MetaFunction<typeof loader> = ({ data }) => {
  return [
    { title: buildTitle('Sauny a saunové doplňky', data?.shopName) },
    {
      name: 'description',
      content:
        'Nabízíme kvalitní sauny, venkovní sauny a sauny na míru pro váš maximální relax. Vše pro dokonalé saunování, včetně kvalitních doplňků.',
    },
    // The site-wide sharing image. It lives here rather than in the document head so a
    // route that knows better — a product with its own OG image, say — replaces it
    // instead of adding a second og:image tag. Remix swaps a leaf route's meta in for
    // the parent's wholesale, so whichever route defines it last wins.
    ...ogImageMeta(
      `${data?.origin ?? ''}${SITE_OG_IMAGE_PATH}`,
      SITE_OG_IMAGE_WIDTH,
      SITE_OG_IMAGE_HEIGHT,
      SITE_OG_IMAGE_TYPE
    ),
  ];
};

// -------------------------
// LOADER (SSR)
// -------------------------
export const loader = async ({ request }: LoaderFunctionArgs) => {
  await checkRedirect(request);

  const cookieHeader = request.headers.get('Cookie');

  // -------------------------
  // Parse existing cookies
  // -------------------------
  const cookies = parseCookieHeader(cookieHeader);

  const bannerClosed = cookies.notification_banner_closed === 'true';

  const parsed = await orderTokenCookie.parse(cookieHeader);
  let token = typeof parsed === 'string' ? parsed : (parsed?.token ?? '');

  // Fallback: přečíst plain text cookie (nastavená klientem přes document.cookie)
  if (!token) {
    token = cookies.orderToken || '';
  }

  const API_URL = process.env.PUBLIC_API_URL!;

  // Client-side API base — the origin browser calls to /api/v2/* and /media/* resolve against.
  // - SAME_ORIGIN_SHOP_API on (prod): the FE host itself, expecting a reverse proxy (Traefik)
  //   that forwards /api/v2 and /media to the BE (NOT all of /api - the FE owns /api/sync-cart etc.). Same-origin lets the BE auth cookies be
  //   host-only (empty JWT_COOKIE_DOMAIN) and still reach SSR, instead of being domained to
  //   the shared parent and leaking across every sibling subdomain. Flip this flag together
  //   with the proxy + the BE dropping JWT_COOKIE_DOMAIN.
  // - Flag off (default, and always in dev): the real API URL, cross-subdomain behaviour.
  const sameOriginApi = process.env.NODE_ENV !== 'development' && process.env.SAME_ORIGIN_SHOP_API === 'true';
  const clientApiUrl = sameOriginApi ? `https://${request.headers.get('host') ?? new URL(API_URL).host}` : API_URL;

  // channels/locale/channelCode resolved per-request (never from process-global state) —
  // this is also what the cached settings calls below are keyed on.
  const { channels, channelConfig, locale, channelCode } = await resolveRequestChannel(request);

  // Find the default channel among filtered channels
  const defaultChannelData = channels.find((ch) => ch.defaultChannel) || channels[0];

  const menuTaxonCode = defaultChannelData?.menuTaxonCode;
  const storeEmail = defaultChannelData?.contactEmail;
  const storePhone = defaultChannelData?.contactPhoneNumber;

  // Resolve double opt-in against the ACTIVE channel — the one the customer's cookie
  // selected and the one the registration request is sent to (Accept-Channel) — so the
  // behaviour the register page predicts matches what the backend actually does. Falls
  // back to the default channel's value (extractChannelConfig) when the cookie points at
  // no known channel.
  const activeChannel = channels.find((ch) => ch.code === channelCode);
  if (activeChannel) {
    channelConfig.accountVerificationRequired = activeChannel.accountVerificationRequired ?? true;
  }

  const currency = cookies.currency ?? channelConfig.defaultCurrency;

  // -------------------------
  // Fetch required data (with correct locale/channel)
  // -------------------------
  const apiHeaders = {
    'Accept-Language': locale,
    'Accept-Channel': channelCode,
  };

  const [taxonRes, notificationBarRes, footerLinks, seoRes] = await Promise.all([
    cachedFetch(
      `${API_URL}/api/v2/shop/taxon-tree/${menuTaxonCode}/branch?imageFilter=sylius_shop_category_thumbnail`,
      {
        headers: apiHeaders,
        ttl: 60,
      }
    ).catch(() => null),
    cachedFetch(`${API_URL}/api/v2/shop/settings/app.notification_bar`, { headers: apiHeaders, ttl: 60 }),
    cachedFetch(`${API_URL}/api/v2/shop/settings/app.footer_links`, { headers: apiHeaders, ttl: 60 }),
    fetch(`${API_URL}/api/v2/shop/settings/app.seo`, { headers: apiHeaders }).catch(() => null),
  ]);

  let notificationContent: NotificationBar | null = null;
  if (notificationBarRes.ok) {
    const notificationData = await notificationBarRes.json();
    const apiNotificationContent = notificationData?.settings ?? null;

    notificationContent =
      !bannerClosed && apiNotificationContent && apiNotificationContent.enabled ? apiNotificationContent : null;
  }

  const taxonTreeData = taxonRes?.ok ? await taxonRes.json() : { 'hydra:member': [] };
  const footerLinksData = footerLinks.ok ? await footerLinks.json() : null;

  // The shop name every page title ends with. It is configured in admin, so it is read
  // here once and handed to the routes through the root match — an untouched settings
  // group serialises as an array, hence the shape check.
  const rawSeoSettings = seoRes?.ok ? ((await seoRes.json())?.settings ?? null) : null;
  const seoSettings: Record<string, string | null> =
    rawSeoSettings && !Array.isArray(rawSeoSettings) ? rawSeoSettings : {};
  const shopName = seoSettings.shop_name ?? '';

  // -------------------------
  // Set cookies if missing
  // -------------------------
  const headers = new Headers();

  // Set-Cookie on every response makes the HTML uncacheable at the CDN — only emit these
  // three when the request doesn't already carry the value we'd set (missing or stale).
  if (cookies.currency !== currency) {
    headers.append('Set-Cookie', `currency=${currency}; Path=/; Max-Age=31536000; SameSite=Lax`);
  }

  if (cookies.channelCode !== channelCode) {
    headers.append('Set-Cookie', `channelCode=${channelCode}; Path=/; Max-Age=31536000; SameSite=Lax`);
  }

  if (cookies.channelDefaultLocale !== channelConfig.defaultLocale) {
    headers.append(
      'Set-Cookie',
      `channelDefaultLocale=${channelConfig.defaultLocale}; Path=/; Max-Age=31536000; SameSite=Lax`
    );
  }

  // BEARER/refresh_token (customer identity, refresh_token issued by the backend's
  // JwtCookieConfig), orderToken (cart), or notification_banner_closed (SSR payload
  // differs per visitor via `bannerClosed` above) all make this response personalised —
  // page-level headers() must never let it become a shared CDN cache entry. A visitor on
  // a non-default channel (CurrencySwitcher only ever writes the `channelCode` cookie —
  // `currency` always follows the hostname's default channel, never the selected one, so
  // no analogous divergence check applies to it) sees channel-specific content too, so a
  // resolved channel other than this hostname's default must also stay private.
  const hasIdentityCookie =
    Boolean(cookies.BEARER) ||
    Boolean(cookies.refresh_token) ||
    Boolean(token) ||
    Boolean(cookies.notification_banner_closed) ||
    channelCode !== channelConfig.defaultChannel;
  setCacheDecision(headers, hasIdentityCookie ? 'private' : 'public');

  // -------------------------
  // i18n (SSR)
  // -------------------------
  const { resources } = await createServerI18n(locale);

  // Behind Coolify/Traefik the Node request is plain http on an internal host; the helper
  // honours X-Forwarded-Proto/Host so canonical, og:url and JSON-LD URLs come out as https.
  const origin = getRequestOrigin(request);

  // -------------------------
  // Final response
  // -------------------------
  return json(
    {
      locale,
      currency,
      i18nResources: resources,
      taxonTree: taxonTreeData['hydra:member'],
      ENV: {
        API_URL: clientApiUrl,
        API_ORIGIN: new URL(clientApiUrl).origin,
        GTM_CONTAINER_ID: process.env.GTM_CONTAINER_ID,
      },
      orderToken: token ?? null,
      channelConfig,
      notificationContent,
      channelCode,
      storeEmail,
      storePhone,
      footerLinksData,
      noIndex: process.env.NOINDEX === 'true',
      origin,
      shopName,
      // The homepage reads its own `homepage_*` fields out of this; fetched once here
      // rather than a second time in that loader.
      seoSettings,
    },
    { headers }
  );
};

// Forwards the public/private cache decision computed above to page-level headers().
export const headers: HeadersFunction = rootCacheHeaders;

function OrganizationJsonLd({
  origin,
  shopName,
  email,
  phone,
}: {
  origin: string;
  shopName: string;
  email?: string;
  phone?: string;
}) {
  const organizationName = 'Anders&Kafka';
  const host = origin.replace(/^https?:\/\//, '');
  const data = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'Organization',
        '@id': `${origin}/#organization`,
        name: organizationName,
        url: origin,
        logo: `${origin}/logo.png`,
        ...(email ? { email } : {}),
        ...(phone ? { telephone: phone } : {}),
      },
      {
        '@type': 'WebSite',
        '@id': `${origin}/#website`,
        // Same admin value (SEO → shop_name) as og:site_name and the title suffix, so Google gets one site name.
        name: shopName || organizationName,
        alternateName: [organizationName, host].filter((name) => name && name !== shopName),
        url: origin,
        publisher: { '@id': `${origin}/#organization` },
        inLanguage: 'cs',
      },
    ],
  };

  // Escape "<" so API-provided values (email/phone) can never break out of the
  // <script> element via a literal "</script>".
  const json = JSON.stringify(data).replace(/</g, '\\u003c');

  return <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: json }} />;
}

function EnvironmentScript({ env }: { env: Record<string, string | undefined> }) {
  return (
    <script
      dangerouslySetInnerHTML={{
        __html: `window.ENV = ${JSON.stringify(env)};`,
      }}
    />
  );
}

function RemixOrderTokenScript({ token }: { token: string | null }) {
  if (!token) return null;
  return (
    <script
      dangerouslySetInnerHTML={{
        __html: `window.__remixOrderToken = "${token}";`,
      }}
    />
  );
}

function App() {
  const data = useLoaderData<{
    locale: string;
    currency: string;
    i18nResources: Record<string, Record<string, string>>;
    taxonTree: Taxon[];
    ENV: Record<string, string | undefined>;
    orderToken: string | null;
    channelConfig: {
      locales: string[];
      defaultLocale: string;
      defaultCurrency: string;
      defaultChannel: string;
      currencies: { channel: string; currency: string }[];
      accountVerificationRequired: boolean;
    };
    notificationContent: string | null;
    noIndex: boolean;
    origin: string;
    shopName: string;
    storeEmail?: string;
    storePhone?: string;
  }>();

  setChannelConfig(data.channelConfig);

  // Self-referencing canonical for every page — pathname only, query params dropped.
  const { pathname } = useLocation();

  // -------------------------
  // SERVER-SIDE INSTANCE (SSR)
  // -------------------------
  // SSR i18n instance (used only on server)
  let i18n;

  // On the server → use SSR instance
  if (typeof window === 'undefined') {
    const instance = createInstance().use(initReactI18next);
    instance.init({
      lng: data.locale,
      fallbackLng: defaultLocale,
      ns: ['translation'],
      defaultNS: 'translation',
      resources: { [data.locale]: data.i18nResources },
      interpolation: { escapeValue: false },
      react: { useSuspense: false },
    });
    i18n = instance;
  } else {
    // On client → initialize synchronously BEFORE render
    if (!window.__i18n) {
      window.__i18n = initClientI18nSync(data.locale, data.i18nResources);
    }
    i18n = window.__i18n;
  }

  return (
    <html lang={data.locale}>
      <head>
        <link rel="dns-prefetch" href={data.ENV.API_ORIGIN} />
        <link rel="preconnect" href={data.ENV.API_ORIGIN} crossOrigin="anonymous" />
        <meta charSet="utf-8" />
        <meta content="initial-scale=1.0, width=device-width" name="viewport" />
        <HydrationWatchdog />
        <InlineStyles />
        <link rel="canonical" href={`${data.origin}${pathname}`} />
        {data.noIndex && <meta name="robots" content="noindex, nofollow" />}
        <OrganizationJsonLd
          origin={data.origin}
          shopName={data.shopName}
          email={data.storeEmail}
          phone={data.storePhone}
        />
        <GTMHeadScript gtmContainerId={data.ENV.GTM_CONTAINER_ID} />
        <Meta />
        <Links />
        <script
          async
          src="https://perf.integritty.dev/rum.js"
          data-key="rum_pk_PgjGczJhifxfDbiP4GmiHJCvl7EhvmIS"
        ></script>
      </head>
      <body>
        <IconSprite />
        <GTMNoScript gtmContainerId={data.ENV.GTM_CONTAINER_ID} />
        <I18nextProvider i18n={i18n}>
          <ProgressBarProvider>
            <QueryClientProvider client={queryClient}>
              <CustomerProvider>
                <OrderProvider>
                  <DropcartProvider>
                    <FlashMessagesProvider>
                      <SlugTranslationsProvider>
                        <GTMPageTracker />
                        <Outlet context={{ taxonTree: data.taxonTree, currency: data.currency }} />
                      </SlugTranslationsProvider>
                    </FlashMessagesProvider>
                  </DropcartProvider>
                </OrderProvider>
              </CustomerProvider>
            </QueryClientProvider>
          </ProgressBarProvider>
        </I18nextProvider>
        <EnvironmentScript env={data.ENV} />
        <RemixOrderTokenScript token={data.orderToken} />
        <ScrollRestoration />
        <Scripts />
      </body>
    </html>
  );
}

// -------------------------
// APP COMPONENT
// -------------------------
export default withSentry(App);

export function ErrorBoundary() {
  const error = useRouteError();
  console.error(error);
  const is404 = isRouteErrorResponse(error) && error.status === 404;

  const rootData = useRouteLoaderData<typeof import('~/root').loader>('root');
  const location = useLocation();
  const targetUrl = location.pathname + location.search + location.hash;
  const recovering = useTransientErrorRecovery(error, targetUrl, (transientError) => {
    captureMessage('transient navigation error: reloading document', {
      level: 'warning',
      tags: { 'sf.kind': 'boundary-reload' },
      extra: { error: String(transientError), targetUrl: location.pathname },
    });
  });

  const channelDefaultLocale = rootData?.channelConfig?.defaultLocale;
  const detectedLocale = detectLocale(location.pathname, channelDefaultLocale);
  const locale = rootData?.locale ?? detectedLocale;
  const resources = rootData?.i18nResources ?? fallbackResources[locale] ?? fallbackResources[defaultLocale];

  const i18n = createInstance().use(initReactI18next);

  i18n.init({
    lng: locale,
    fallbackLng: defaultLocale,
    ns: ['translation'],
    defaultNS: 'translation',
    resources: { [locale]: resources },
    interpolation: { escapeValue: false },
    react: { useSuspense: false },
  });

  const t = i18n.t.bind(i18n);

  const metaTitle = recovering
    ? t('common.loading')
    : is404
      ? `404 - ${t('pages.error.404.title')}`
      : t('pages.error.default.title');

  const metaDescription = is404 ? t('pages.error.404.description') : t('pages.error.default.description');

  if (!recovering) captureRemixErrorBoundaryError(error);

  return (
    <html lang={locale}>
      <head>
        {rootData?.ENV?.API_ORIGIN && (
          <>
            <link rel="dns-prefetch" href={rootData.ENV.API_ORIGIN} />
            <link rel="preconnect" href={rootData.ENV.API_ORIGIN} crossOrigin="anonymous" />
          </>
        )}
        <meta charSet="utf-8" />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <HydrationWatchdog />
        <InlineStyles />
        {rootData?.origin && <link rel="canonical" href={`${rootData.origin}${location.pathname}`} />}
        {rootData?.noIndex && <meta name="robots" content="noindex, nofollow" />}
        <title>{metaTitle}</title>
        <meta name="description" content={metaDescription} />
        <Meta />
        <Links />
      </head>
      <body>
        <IconSprite />
        <I18nextProvider i18n={i18n}>
          <ProgressBarProvider>
            <QueryClientProvider client={queryClient}>
              <CustomerProvider>
                <OrderProvider>
                  <DropcartProvider>
                    <SlugTranslationsProvider>
                      {rootData ? (
                        <Layout>
                          <Container>
                            <ErrorBoundaryRoute is404={is404} recovering={recovering} />
                          </Container>
                        </Layout>
                      ) : (
                        <Container className="py-5">
                          <ErrorBoundaryRoute is404={is404} recovering={recovering} />
                        </Container>
                      )}
                    </SlugTranslationsProvider>
                  </DropcartProvider>
                </OrderProvider>
              </CustomerProvider>
            </QueryClientProvider>
          </ProgressBarProvider>
        </I18nextProvider>
        <EnvironmentScript env={rootData?.ENV || {}} />
        <RemixOrderTokenScript token={rootData?.orderToken} />
        <ScrollRestoration />
        <Scripts />
      </body>
    </html>
  );
}
